import { RowClickedEvent } from 'ag-grid-community';
import { useCallback, useRef } from 'react';

export interface ShiftRangeSelectionConfig<TData> {
    enableShiftRangeSelect?: boolean; //Shift+클릭 범위 선택 기능 활성화 여부 true면 공통 훅의 Shift 범위선택/토글 로직 활성화 false 이면기존 onRowClicked 
    selectedIds: Array<string | number>; //현재 선택된 행 ID 목록
    setSelectedIds: (ids: Array<string | number>) => void; 
    idKey?: keyof TData; //행 ID 계산을 위한 기본 필드명 예: 'valueIndex', 'lotNo'
    resolveId?: (data: TData) => string | number | null | undefined; //idKey 대신 커스텀 ID 계산이 필요할 때 사용 예: 복합키(A-B-C) 생성, 숫자→문자 변환
    isRowSelectable?: (params: RowClickedEvent<TData>) => boolean; //행 선택 가능 여부 검증 예: 삭제행, 잠금행, 클릭 가드(clickable) 조건 차단
    isRangeNodeSelectable?: (params: { id: string | number; data: TData; rowIndex: number }) => boolean; //범위 선택 가능 여부 검증 
}

interface UseShiftRangeRowSelectionParams<TData> {
    config?: ShiftRangeSelectionConfig<TData>;  //Shift 선택 동작에 필요한 설정 객체
    onRowClicked?: (event: RowClickedEvent<TData>) => void; //공통 로직 비활성/미사용일 때 위임할 기존 클릭 핸들러
}

export const useShiftRangeRowSelection = <TData extends {} = any>({
    config,
    onRowClicked,
}: UseShiftRangeRowSelectionParams<TData>) => {
    // Shift+클릭 범위 선택의 시작점(앵커) 행 인덱스
    const lastClickedRowIndexRef = useRef<number | null>(null);

    return useCallback((event: RowClickedEvent<TData>) => {
        // 기능 비활성 시에는 기존 onRowClicked 동작에 위임
        if (!config?.enableShiftRangeSelect) {
            onRowClicked?.(event);
            return;
        }

        // 하단 고정 행/빈 데이터, 화면별 선택 불가 조건은 제외
        if (event.node?.rowPinned === 'bottom' || !event.data) return;
        if (config.isRowSelectable && !config.isRowSelectable(event)) return;

        const currentIndex = event.node?.rowIndex;
        const isShift = !!(event.event as MouseEvent | undefined)?.shiftKey;
        const clickedId = config.resolveId
            ? config.resolveId(event.data)
            : (config.idKey ? event.data[config.idKey] : undefined) as string | number | null | undefined;
        if (clickedId === null || clickedId === undefined || clickedId === '') return;

        // Shift+클릭이면 현재 화면(필터/정렬 반영) 기준으로 범위 선택
        if (isShift && lastClickedRowIndexRef.current !== null && typeof currentIndex === 'number') {
            const start = Math.min(lastClickedRowIndexRef.current, currentIndex);
            const end = Math.max(lastClickedRowIndexRef.current, currentIndex);
            const rangeIds: Array<string | number> = [];

            event.api.forEachNodeAfterFilterAndSort((node: any) => {
                if (typeof node?.rowIndex !== 'number') return;
                if (!node.data || node.rowPinned === 'bottom') return;
                if (node.rowIndex < start || node.rowIndex > end) return;

                const rangeId = config.resolveId
                    ? config.resolveId(node.data as TData)
                    : (config.idKey ? (node.data as TData)[config.idKey] : undefined) as string | number | null | undefined;
                if (rangeId === null || rangeId === undefined || rangeId === '') return;

                if (
                    config.isRangeNodeSelectable &&
                    !config.isRangeNodeSelectable({
                        id: rangeId,
                        data: node.data as TData,
                        rowIndex: node.rowIndex,
                    })
                ) {
                    return;
                }
                rangeIds.push(rangeId);
            });

            const next = Array.from(new Set([...(config.selectedIds || []), ...rangeIds]));
            config.setSelectedIds(next);
            return;
        }

        // 일반 클릭은 단건 토글 선택
        const current = config.selectedIds || [];
        const isSelected = current.includes(clickedId);
        const next = isSelected
            ? current.filter((id) => id !== clickedId)
            : [...current, clickedId];
        config.setSelectedIds(next);

        // 다음 Shift 범위 선택을 위한 앵커 저장
        if (typeof currentIndex === 'number') {
            lastClickedRowIndexRef.current = currentIndex;
        }
    }, [config, onRowClicked]);
};
